import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { gray, semantic } from 'sutra/tokens/colors'

// ── Public types ──────────────────────────────────────────────────────────────

export interface ChartHoverAnnotationRow {
  /** Left label text, e.g. "5", "Positive", "United States", "Rating", "Records". */
  label: string
  /** Pre-formatted value string, e.g. "12,023", "4.55", "9.3%". */
  value: string
  /** Parenthetical share shown after the value, e.g. "78.2%". null = omit. */
  percentage: string | null
  /** Text tint for this row (rating / sentiment / series color). null = default. */
  color: string | null
}

export interface ChartDatapointHoverAnnotationProps {
  /** Heading: theme name, series category, or time label ("Sep 2025"). */
  title: string
  /** Body rows — one per metric or breakdown slice. */
  rows: ChartHoverAnnotationRow[]
  /** Summary footer row, e.g. "Total: 15,369". null = no footer. Always bold. */
  footer: ChartHoverAnnotationRow | null
  /**
   * Row label that is currently focused (matches `ChartHoverAnnotationRow.label`).
   * null = no focus; all rows render in normal weight without highlight.
   * When set, the matching row gets a light-gray background and both its label
   * and value render at weight 600.
   */
  focusedField: string | null
}

// ── Styled subcomponents ──────────────────────────────────────────────────────

/**
 * Outer shell: fixed width, max-height, overflow hidden so the title and footer
 * pin while only the inner scroll region moves.
 * No padding here — each section manages its own horizontal inset so the focused
 * row background can run edge-to-edge.
 */
const AnnotationContainer = styled.div`
  background: ${semantic.bg};
  border: 1px solid ${semantic.borderStrong};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  width: fit-content;
  min-width: 200px;
  max-width: 280px;
  box-sizing: border-box;
  max-height: 280px;
  overflow: hidden;
`

const AnnotationTitle = styled.p`
  flex-shrink: 0;
  margin: 0;
  padding: 8px 12px 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${semantic.fg};
  border-bottom: 1px solid ${semantic.border};
`

/** Scrollable region — only the rows scroll; title and footer stay pinned. */
const AnnotationScroll = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0 0 4px;
`

const AnnotationRow = styled.div<{ $focused: boolean }>`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  column-gap: 12px;
  padding: 1px 12px;
  border-radius: 0;
  background: ${(p) => (p.$focused ? gray[100] : 'transparent')};
`

const AnnotationRowLabel = styled.span<{
  $color: string | null
  $focused: boolean
}>`
  font-size: 13px;
  font-weight: ${(p) => (p.$focused ? 600 : 400)};
  color: ${(p) => p.$color ?? semantic.fg};
`

/** Groups value + percentage so they stay together at the right edge. */
const AnnotationRowValues = styled.span<{
  $focused: boolean
  $color: string | null
}>`
  display: flex;
  align-items: baseline;
  column-gap: 4px;
  white-space: nowrap;
  font-size: 13px;
  font-weight: ${(p) => (p.$focused ? 600 : 400)};
  color: ${(p) => p.$color ?? semantic.fg};
`

const AnnotationRowPercentage = styled.span<{ $color: string | null }>`
  font-size: 13px;
  color: ${(p) => p.$color ?? semantic.fgMuted};
`

const AnnotationFooter = styled.div`
  flex-shrink: 0;
  padding: 4px 12px 8px;
  border-top: 1px solid ${semantic.border};
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  column-gap: 12px;
`

const AnnotationFooterLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${semantic.fg};
`

const AnnotationFooterValue = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${semantic.fg};
  white-space: nowrap;
`

// ── Component ─────────────────────────────────────────────────────────────────

// bully ignore[no-generic-identifier-data: user-mandated component name; "Datapoint" is chart domain vocabulary; permanent: name is the public contract]
const ChartDatapointHoverAnnotation: React.FC<
  ChartDatapointHoverAnnotationProps
> = ({ title, rows, footer, focusedField }) => {
  const focusedRowRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    focusedRowRef.current?.scrollIntoView({ block: 'nearest' })
  }, [focusedField])

  return (
    <AnnotationContainer>
      <AnnotationTitle>{title}</AnnotationTitle>

      <AnnotationScroll>
        {rows.map((row, i) => {
          const isFocused = row.label === focusedField
          return (
            <AnnotationRow
              key={i}
              $focused={isFocused}
              ref={isFocused ? focusedRowRef : null}
            >
              <AnnotationRowLabel $color={row.color} $focused={isFocused}>
                {row.label}:
              </AnnotationRowLabel>
              <AnnotationRowValues $focused={isFocused} $color={row.color}>
                {row.value}
                {row.percentage !== null && (
                  <AnnotationRowPercentage $color={row.color}>
                    ({row.percentage})
                  </AnnotationRowPercentage>
                )}
              </AnnotationRowValues>
            </AnnotationRow>
          )
        })}
      </AnnotationScroll>

      {footer !== null && (
        <AnnotationFooter>
          <AnnotationFooterLabel>{footer.label}:</AnnotationFooterLabel>
          <AnnotationFooterValue>
            {footer.value}
            {footer.percentage !== null ? ` (${footer.percentage})` : ''}
          </AnnotationFooterValue>
        </AnnotationFooter>
      )}
    </AnnotationContainer>
  )
}

export { ChartDatapointHoverAnnotation }
